import { API } from 'aws-amplify';

/**
 * List all users in the Cognito User Pool
 * @param {number} limit - Number of users to return (default 60)
 * @param {string} paginationToken - Token for pagination
 * @returns {Promise<{users: Array, paginationToken: string}>}
 */
export const listUsers = async (limit = 60, paginationToken = null) => {
  const query = `
    query ListUsers($limit: Int, $paginationToken: String) {
      listUsers(limit: $limit, paginationToken: $paginationToken) {
        users {
          username
          email
          name
          phoneNumber
          emailVerified
          enabled
          status
          created
          modified
          groups
        }
        paginationToken
      }
    }
  `;

  try {
    const result = await API.graphql({
      query,
      variables: { limit, paginationToken }
    });
    return result.data.listUsers;
  } catch (error) {
    console.error('Error listing users:', error);
    throw error;
  }
};

/**
 * Create a new user in Cognito
 * @param {string} email - Email address
 * @param {string} name - Full name (optional)
 * @param {string} phoneNumber - Phone number (optional)
 * @returns {Promise<object>}
 */
export const createUser = async (email, name = null, phoneNumber = null) => {
  const mutation = `
    mutation CreateUser($email: String!, $name: String, $phoneNumber: String) {
      createUser(email: $email, name: $name, phoneNumber: $phoneNumber) {
        success
        message
        data
      }
    }
  `;

  try {
    const result = await API.graphql({
      query: mutation,
      variables: { email, name, phoneNumber }
    });
    return result.data.createUser;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update user attributes
 * @param {string} username - Username of the user to update
 * @param {string} email - Email address (optional)
 * @param {string} name - Full name (optional)
 * @param {string} phoneNumber - Phone number (optional)
 * @returns {Promise<object>}
 */
export const updateUser = async (username, email = null, name = null, phoneNumber = null) => {
  const mutation = `
    mutation UpdateUser($username: String!, $email: String, $name: String, $phoneNumber: String) {
      updateUser(username: $username, email: $email, name: $name, phoneNumber: $phoneNumber) {
        success
        message
        data
      }
    }
  `;

  try {
    const result = await API.graphql({
      query: mutation,
      variables: { username, email, name, phoneNumber }
    });
    return result.data.updateUser;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Set user account status (enable or disable)
 * @param {string} username - Username to update
 * @param {boolean} enabled - True to enable, false to disable
 * @returns {Promise<object>}
 */
export const setUserStatus = async (username, enabled) => {
  const mutation = `
    mutation SetUserStatus($username: String!, $enabled: Boolean!) {
      setUserStatus(username: $username, enabled: $enabled) {
        success
        message
        data
      }
    }
  `;

  try {
    const result = await API.graphql({
      query: mutation,
      variables: { username, enabled }
    });
    return result.data.setUserStatus;
  } catch (error) {
    console.error('Error setting user status:', error);
    throw error;
  }
};

/**
 * Delete a user
 * @param {string} username - Username to delete
 * @returns {Promise<object>}
 */
export const deleteUser = async (username) => {
  const mutation = `
    mutation DeleteUser($username: String!) {
      deleteUser(username: $username) {
        success
        message
        data
      }
    }
  `;

  try {
    const result = await API.graphql({
      query: mutation,
      variables: { username }
    });
    return result.data.deleteUser;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * List all groups in the Cognito User Pool
 * @returns {Promise<string[]>}
 */
export const listGroups = async () => {
  const query = `
    query ListGroups {
      listGroups
    }
  `;
  try {
    const result = await API.graphql({ query });
    return result.data.listGroups;
  } catch (error) {
    console.error('Error listing groups:', error);
    throw error;
  }
};

/**
 * Update a user's group memberships
 * @param {string} username - Username to update
 * @param {string[]} groups - Desired group names (replaces current memberships)
 * @returns {Promise<object>}
 */
export const updateUserGroups = async (username, groups) => {
  const mutation = `
    mutation UpdateUserGroups($username: String!, $groups: [String!]!) {
      updateUserGroups(username: $username, groups: $groups) {
        success
        message
        data
      }
    }
  `;
  try {
    const result = await API.graphql({ query: mutation, variables: { username, groups } });
    return result.data.updateUserGroups;
  } catch (error) {
    console.error('Error updating user groups:', error);
    throw error;
  }
};

/**
 * Reset user password - Sends password reset email with verification code
 * User must complete the standard forgot-password flow
 * @param {string} username - Username to reset password for
 * @returns {Promise<object>}
 */
export const resetPassword = async (username) => {
  const mutation = `
    mutation ResetPassword($username: String!) {
      resetPassword(username: $username) {
        success
        message
        data
      }
    }
  `;

  try {
    const result = await API.graphql({
      query: mutation,
      variables: { username }
    });
    return result.data.resetPassword;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};
